import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigation } from '../contexts/NavigationContext';
import { 
    FaArrowLeft, FaUserGraduate, FaChalkboardTeacher,
    FaSignInAlt, FaUserPlus, FaHome 
} from 'react-icons/fa';
import PageLayout from '../components/PageLayout';

const LoginPage = () => {
    const { login } = useAuth();
    const { navigateTo } = useNavigation();
    
    const [view, setView] = useState('selection'); 
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');

    const handleLogin = async (e) => {
        e.preventDefault();
        setError(''); 
        
        // 1. DETERMINE INTENDED ROLE
        // If the user is on the "Student Login" screen, they are claiming to be a student.
        const intendedRole = view === 'login-student' ? 'student' : 'teacher';

        try {
            // 2. PASS ROLE TO LOGIN FUNCTION
            await login(username, password, intendedRole);
            
            // Navigate to dashboard on success
            navigateTo('dashboard');
        } catch (e) { 
            console.error("Login Error:", e);
            // Display the specific error from the server (e.g., "Access Denied: You are a Student")
            setError(e.message || "Invalid Username or Password"); 
        }
    };

    if (view === 'selection') {
        return (
            <PageLayout>
                <div className="flex flex-col items-center justify-center min-h-[70vh]">
                    <div className="w-full max-w-4xl mb-6">
                        <button onClick={() => navigateTo('landing')} className="flex items-center text-gray-500 font-bold hover:text-blue-600 transition-colors">
                            <FaHome className="mr-2" /> Back to Home
                        </button>
                    </div>
                    <div className="text-center mb-10">
                        <h1 className="text-4xl md:text-6xl font-black text-gray-800 tracking-tight mb-2 drop-shadow-sm">Welcome to <span className="text-blue-600">BouncePen</span></h1>
                        <p className="text-gray-500 font-bold text-sm uppercase tracking-widest">Choose your portal</p>
                    </div>
                    <div className="w-full max-w-4xl grid md:grid-cols-2 gap-8">
                        <div className="bg-white/80 backdrop-blur rounded-2xl p-8 shadow-xl border-t-4 border-blue-500 hover:scale-105 transition-transform duration-300">
                            <div className="flex flex-col items-center text-center mb-5">
                                <div className="w-20 h-20 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-4xl mb-4 shadow-inner"><FaUserGraduate /></div>
                                <h2 className="text-3xl font-black text-gray-800">Student</h2>
                            </div>
                            <div className="space-y-4">
                                <button onClick={() => setView('login-student')} className="w-full bg-blue-600 hover:bg-blue-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg text-lg transition-all"><FaSignInAlt/> Log In</button>
                                <button onClick={() => navigateTo('register', { role: 'student' })} className="w-full bg-white text-blue-600 border-2 border-blue-100 hover:border-blue-300 py-3 rounded-xl font-bold flex items-center justify-center gap-2 text-sm transition-all"><FaUserPlus/> New Account</button>
                            </div>
                        </div>
                        <div className="bg-white/80 backdrop-blur rounded-2xl p-8 shadow-xl border-t-4 border-purple-500 hover:scale-105 transition-transform duration-300">
                            <div className="flex flex-col items-center text-center mb-5">
                                <div className="w-20 h-20 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center text-4xl mb-4 shadow-inner"><FaChalkboardTeacher /></div>
                                <h2 className="text-3xl font-black text-gray-800">Teacher</h2>
                            </div>
                            <div className="space-y-4">
                                <button onClick={() => setView('login-teacher')} className="w-full bg-purple-600 hover:bg-purple-700 text-white py-4 rounded-xl font-bold flex items-center justify-center gap-2 shadow-lg text-lg transition-all"><FaSignInAlt/> Log In</button>
                                <button onClick={() => navigateTo('register', { role: 'teacher' })} className="w-full bg-white text-purple-600 border-2 border-purple-100 hover:border-purple-300 py-3 rounded-xl font-bold flex items-center justify-center gap-2 text-sm transition-all"><FaUserPlus/> New Account</button>
                            </div>
                        </div>
                    </div>
                </div>
            </PageLayout>
        );
    }

    const isStudent = view === 'login-student';
    
    return (
        <PageLayout>
            <div className="flex flex-col items-center justify-center min-h-[60vh]">
                <div className="w-full max-w-md relative z-50"> 
                    <button onClick={() => { setView('selection'); setError(''); setUsername(''); setPassword(''); }} className="mb-6 flex items-center text-gray-500 font-bold hover:text-gray-900 bg-white/50 px-4 py-2 rounded-full shadow-sm text-sm transition-colors">
                        <FaArrowLeft className="mr-2"/> Back to Selection
                    </button>

                    <div className={`bg-white/90 backdrop-blur p-10 rounded-3xl shadow-2xl border-t-8 ${isStudent ? 'border-blue-500' : 'border-purple-500'}`}>
                        <div className="text-center mb-8">
                            <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center mb-4 ${isStudent ? 'bg-blue-100 text-blue-600' : 'bg-purple-100 text-purple-600'}`}>
                                {isStudent ? <FaUserGraduate className="text-3xl"/> : <FaChalkboardTeacher className="text-3xl"/>}
                            </div>
                            <h2 className="text-3xl font-black text-gray-800">{isStudent ? 'Student Login' : 'Teacher Login'}</h2>
                        </div>
                        {error && <div className="bg-red-50 border-l-4 border-red-500 text-red-700 p-4 mb-6 font-bold rounded shadow-sm text-center">⚠️ {error}</div>}
                        <form onSubmit={handleLogin} className="space-y-5">
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase ml-1 tracking-wider mb-1 block">Username</label>
                                <input className="w-full p-4 border-2 border-gray-200 rounded-xl outline-none font-bold text-gray-700 bg-white focus:ring-4 transition-all text-base" placeholder="Enter username..." value={username} onChange={(e) => setUsername(e.target.value)} required autoComplete="off" />
                            </div>
                            <div>
                                <label className="text-xs font-bold text-gray-500 uppercase ml-1 tracking-wider mb-1 block">Password</label>
                                <input className="w-full p-4 border-2 border-gray-200 rounded-xl outline-none font-bold text-gray-700 bg-white focus:ring-4 transition-all text-base" type="password" placeholder="Enter password..." value={password} onChange={(e) => setPassword(e.target.value)} required />
                            </div>
                            <button className={`w-full py-4 rounded-xl text-white font-bold text-xl shadow-lg hover:opacity-90 transition-all transform hover:-translate-y-1 mt-4 ${isStudent ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'}`}>Log In Now</button>
                        </form>
                    </div>
                </div>
            </div>
        </PageLayout>
    );
};

export default LoginPage;